import { useTranslate } from '@tolgee/react';
import React, { useState } from 'react';
import { WHATSAPP_TEMPLATE_BG_COLOR } from '../../config/constants';
import TemplateCard from './templateCard';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import {
  sendWhasappTemplateMessage,
  toggleTemplateModal
} from '../../redux/slices/whatsappTemplate';
import {
  addLock,
  addPandingMessage,
  removeLock,
  updatePandingMessageUuid
} from '../../redux/slices/communication';
import { ADDITIONAL_DATA_TYPE_WHATSAPP_TEMPLATE } from '../communication/constants';
import { DetailViewHeader } from './components/DetailViewHeader';
import toast from 'react-hot-toast';
import { dispatch } from '../../redux/store';
import { isValidURL } from './helpers';

interface ComponentType {
  type: string;
  format?: string;
  text?: string;
  example?: {
    header_text?: string[];
    body_text?: string[][];
    header_handle?: string[];
  };
  buttons?: { type: string; text: string; url?: string }[];
}

interface TemplateProps {
  template: {
    id: string;
    name: string;
    language: string;
    category: string;
    status: string;
    components: ComponentType[];
  };
  submitRef: any;
  submitting: boolean;
  setSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

const SendTemplate: React.FC<TemplateProps> = ({
  template,
  submitRef,
  submitting,
  setSubmitting
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>();

  const {
    data: {
      activeConversation,
      urls,
      conversationDetails: { data }
    }
  } = useSelector((state: any) => state.communication);

  const { openTemplateModal } = useSelector(
    (state: any) => state.WhatsappTemplateBuilder
  );

  const header = template.components.find((c) => c.type === 'HEADER');
  const body = template.components.find((c) => c.type === 'BODY');
  const buttons = template.components.find((c) => c.type === 'BUTTONS');

  const returnPlaceholderArray = (text: string) => {
    return text?.match(/{{[^{}]+}}/g) || [];
  };
  const extractPlaceholders = (text: string) => {
    return text?.replace(/{{(\d+|[\w_]+)}}/g, (_, match) => match);
  };
  const checkIfNumber = (value: any): boolean => {
    return !isNaN(Number(value));
  };
  const replacePlaceholders = (text: string, values?: any[]) => {
    if (!values) return text;

    return text.replace(/{{(\d+|[\w_]+)}}/g, (match, key) => {
      const numericIndex = parseInt(key, 10) - 1;

      // If it's a number, replace it with the corresponding text from values
      if (!isNaN(numericIndex) && values[numericIndex]?.text) {
        return values[numericIndex].text;
      }

      // If it's a named parameter, find and replace it
      const namedParam = values.find((p) => p.parameter_name === key);
      if (namedParam) {
        return namedParam.text;
      }

      // If no match is found, return the original placeholder
      return match;
    });
  };

  const [sendTemplateForm, setSendTemplateForm] = useState(
    [
      header &&
        returnPlaceholderArray(header?.text)?.length > 0 && {
          type: 'header',
          parameters: []
        },
      body &&
        returnPlaceholderArray(body?.text)?.length > 0 && {
          type: 'body',
          parameters: []
        },
      ...(buttons
        ? buttons.buttons
            .map((btn, originalIndex) =>
              btn?.type === 'URL' && returnPlaceholderArray(btn?.url).length > 0
                ? {
                    type: 'button',
                    sub_type: 'url',
                    index: originalIndex, // Keep original index
                    parameters: []
                  }
                : null
            )
            .filter(Boolean) // Remove null values
        : [])
    ].filter(Boolean)
  ); // Remove falsy values

  const [sendTemplateMediaForm, setSendTemplateMediaForm] = useState(
    [
      header &&
        header?.format !== 'TEXT' && {
          type: 'header',
          parameters: []
        }
    ].filter(Boolean)
  );

  const updateParameter = (
    type: string,
    index: number,
    value: string,
    placeholder: string
  ) => {
    const isNumber = /^\d+$/.test(placeholder.replace(/{{|}}/g, ''));
    const parameter_name = isNumber ? null : placeholder.replace(/{{|}}/g, '');

    setSendTemplateForm((prevState) =>
      prevState.map((section) =>
        section?.type === type
          ? {
              ...section,
              parameters: [
                ...section.parameters.slice(0, index),
                isNumber
                  ? { type: 'text', text: value }
                  : { parameter_name, type: 'text', text: value }, // Handling named parameters
                ...section.parameters.slice(index + 1)
              ]
            }
          : section
      )
    );
  };

  const updateMediaParameter = (
    type: string,
    index: number,
    value: string,
    format: string
  ) => {
    const media_type =
      format === 'IMAGE' ? 'image' : format === 'VIDEO' ? 'video' : 'document';

    setSendTemplateMediaForm((prevState) =>
      prevState.map((section) =>
        section?.type === type
          ? {
              ...section,
              parameters: [
                ...section.parameters.slice(0, index),

                { type: media_type, [media_type]: { link: value } },
                ...section.parameters.slice(index + 1)
              ]
            }
          : section
      )
    );
  };

  const updateButtonVariable = (
    btnIndex: number,
    index: number,
    value: string,
    placeholder: string
  ) => {
    const isNumber = /^\d+$/.test(placeholder.replace(/{{|}}/g, ''));
    const parameter_name = isNumber ? null : placeholder.replace(/{{|}}/g, '');

    setSendTemplateForm((prevState) =>
      prevState.map((section) =>
        section?.type === 'button' &&
        'index' in section &&
        section.index === btnIndex
          ? {
              ...section,
              parameters: [
                ...section.parameters.slice(0, index),
                isNumber
                  ? { type: 'text', text: value }
                  : { parameter_name, type: 'text', text: value }, // Handling named parameters
                ...section.parameters.slice(index + 1)
              ]
            }
          : section
      )
    );
  };

  const updatedTemplate = {
    ...template,
    components: template?.components?.map((component) => {
      if (component.type === 'HEADER') {
        const headerParams =
          sendTemplateForm.find((x) => x.type === 'header')?.parameters || [];

        return {
          ...component,
          example: {
            ...component.example,
            ...(headerParams.some((p) => p.parameter_name)
              ? {
                  header_text_named_params: headerParams
                    .filter((p) => p.parameter_name)
                    .map((item) => ({
                      param_name: item?.parameter_name,
                      example: item?.text
                    }))
                }
              : { header_text: headerParams.map((p) => p.text) })
          }
        };
      }

      if (component.type === 'BODY') {
        const bodyParams =
          sendTemplateForm.find((x) => x.type === 'body')?.parameters || [];

        return {
          ...component,
          example: {
            ...component.example,
            ...(bodyParams.some((p) => p.parameter_name)
              ? {
                  body_text_named_params: bodyParams
                    .filter((p) => p.parameter_name)
                    .map((item) => ({
                      param_name: item?.parameter_name,
                      example: item?.text
                    }))
                }
              : { body_text: [bodyParams.map((p) => p.text)] })
          }
        };
      }

      // Handling BUTTONS
      if (component.type === 'BUTTONS') {
        const buttonParamsList = sendTemplateForm.filter(
          (x) => x.type === 'button' && 'index' in x
        ) as Array<{ type: string; index: number; parameters: any[] }>;

        return {
          ...component,
          buttons: component.buttons.map((button, index) => {
            const buttonParams =
              buttonParamsList.find((x) => x.index === index)?.parameters || [];

            return {
              ...button,
              url: button.url
                ? replacePlaceholders(button.url, buttonParams)
                : button.url
            };
          })
        };
      }

      return component;
    })
  };

  const successCallback = (id, uuid, conversationUuid) => {
    dispatch(updatePandingMessageUuid(conversationUuid, id, uuid));
    dispatch(removeLock(conversationUuid, id, uuid));
  };

  const sendMessage = () => {
    console.log('submit button clicked');
    const submitFormat = {
      type: ADDITIONAL_DATA_TYPE_WHATSAPP_TEMPLATE,
      data: {
        template_data: template,
        whatsapp_template_sending: {
          template: {
            name: template.name,
            language: {
              code: template.language
            },
            components: [...sendTemplateForm, ...sendTemplateMediaForm]
          }
        }
      }
    };

    // check if content is empty
    console.log('submitted form', [
      ...sendTemplateForm,
      ...sendTemplateMediaForm
    ]);
    const isFormValid = sendTemplateForm.every((section) => {
      // Check if parameters exist
      if (section.parameters.length === 0) {
        return false;
      }

      // If section is NOT 'buttons', check for URL and validate placeholder count
      if (section.type !== 'button') {
        const currentSection = section.type === 'header' ? header : body;

        // Check if any parameter contains a URL
        if (
          section.parameters.some((item) => {
            const isUrl = isValidURL(item?.text || item?.example);
            if (isUrl) {
              toast.error(t('whatsappTemplate.SendTemplate.noUrlsAccespted'), {
                position: 'top-right',
                id: 'noUrlAccepted'
              });
              return true; // Found a URL, form should be invalid
            }
            return false;
          })
        ) {
          return false;
        }

        // Validate placeholder count
        return (
          section.parameters.filter((item) => item?.text || item?.example)
            .length === returnPlaceholderArray(currentSection?.text)?.length
        );
      }

      // If section IS 'button', validate based on `url`
      if (section.type === 'button') {
        //@ts-ignore
        const currentSection = buttons.buttons[section?.index];
        return (
          section.parameters.filter((item) => item?.text || item?.example)
            .length === returnPlaceholderArray(currentSection?.url)?.length
        );
      }

      return true;
    });

    console.log('form valid', isFormValid);
    if (!isFormValid) {
      toast.error(t('whatsappTemplate.SendTemplate.fillAllVariables'), {
        position: 'top-right'
      });
      return;
    }

    if (submitting) return;

    // get text from contents:

    const tmpID = Date.now();
    const convUuid = activeConversation.uuid;
    setSubmitting(true);
    dispatch(addLock(tmpID));

    const newMessage = {
      type: 'message',
      is_sent_by_me: true,
      attachment: [],
      created: null,
      author: null,
      message_content_html: {
        ops: [{ insert: 'template' }]
      },
      is_liked_by_me: false,
      answers: [],
      social_media_platform_user:
        activeConversation?.social_media_platform_user,
      message_content: 'template',
      uuid: null,
      platform_created: null,
      total_reactions_count: 0,
      modified: null,
      editing_allowed: false,
      is_internal_note: false,
      additional_data: submitFormat,
      tmpID
    };
    dispatch(
      addPandingMessage({ message: newMessage, uuid: activeConversation.uuid })
    );

    dispatch(
      sendWhasappTemplateMessage(
        activeConversation.uuid,
        data?.data?.[0]?.uuid,
        false,
        (uuid) => successCallback(tmpID, uuid, convUuid),
        submitFormat
      )
    );
    setSubmitting(false);

    const k = 'whatsappTemplate.SendTemplateDetails.successMessage';
    toast.success(t(k), {
      position: 'top-right',
      id: k
    });
    dispatch(toggleTemplateModal(!openTemplateModal));
  };

  console.log('form', sendTemplateForm, sendTemplateMediaForm, updatedTemplate);

  return (
    <div className="w-100 h-100 d-flex gap-3 align-items-center">
      <div
        style={{ overflowY: 'auto' }}
        className="flex-fill w-100 p-1 d-flex flex-column gap-3 align-items-center h-100"
      >
        <div className={'my-5 d-flex justify-content-center'}>
          <div className={'text-muted mw-400px text-center'}>
            {t('whatsappTemplate.SendTemplateDetails.infoMessage')}
          </div>
        </div>

        {header?.format === 'TEXT' &&
          returnPlaceholderArray(header.text).length > 0 && (
            <div className="w-100 bg-white rounded-2 p-3 py-4 d-flex flex-column gap-2 ">
              <DetailViewHeader
                title={t('whatsappTemplate.SendTemplateDetails.header')}
              />

              <div className="d-flex flex-column gap-2 mb-3">
                {returnPlaceholderArray(header.text || '').map(
                  (item, index) => {
                    const placeholder = extractPlaceholders(item);
                    const isNumber = checkIfNumber(item);
                    console.log('placeholder here', isNumber, placeholder);
                    return (
                      <div
                        key={index}
                        className="d-flex gap-1 w-100 text-muted align-items-center"
                      >
                        <span>{item}</span>:
                        <input
                          className="form-control form-control-sm"
                          value={
                            sendTemplateForm.find((x) => x.type === 'header')
                              ?.parameters[index]?.example ||
                            sendTemplateForm.find((x) => x.type === 'header')
                              ?.parameters[index]?.text ||
                            ''
                          }
                          onChange={(e) =>
                            updateParameter(
                              'header',
                              index,
                              e.target.value,
                              item
                            )
                          }
                        />
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          )}

        {header?.format !== 'TEXT' && (
          <div className="w-100 bg-white rounded-2 p-3 py-4 d-flex flex-column gap-2 ">
            <DetailViewHeader
              title={t('whatsappTemplate.SendTemplateDetails.header')}
            />

            <div className="d-flex flex-column gap-2 mb-3">
              <div className="d-flex gap-1 w-100 text-muted align-items-center">
                <span>
                  {t('whatsappTemplate.SendTemplateDetails.link')}
                </span>
                :
                <input
                  className="form-control form-control-sm"
                  value={
                    sendTemplateMediaForm.find((x) => x.type === 'header')
                      ?.parameters[0]?.[
                      header?.format === 'IMAGE'
                        ? 'image'
                        : header?.format === 'VIDEO'
                        ? 'video'
                        : 'document'
                    ]?.link
                  }
                  onChange={(e) =>
                    updateMediaParameter(
                      'header',
                      0,
                      e.target.value,
                      header?.format
                    )
                  }
                />
              </div>
            </div>
          </div>
        )}

        {body?.text && returnPlaceholderArray(body.text).length > 0 && (
          <div className="w-100 bg-white rounded-2 p-3 py-4 d-flex flex-column gap-2 ">
            <DetailViewHeader
              title={t('whatsappTemplate.SendTemplateDetails.body')}
            />

            {/* Header placeholders */}

            <div className="d-flex flex-column gap-2 mb-3">
              {returnPlaceholderArray(body.text || '').map((item, index) => {
                const placeholder = extractPlaceholders(item);
                return (
                  <div
                    key={index}
                    className="d-flex gap-1 w-100 text-muted align-items-center"
                  >
                    <span>{item}</span>:
                    <input
                      className="form-control form-control-sm"
                      value={
                        sendTemplateForm.find((x) => x.type === 'body')
                          ?.parameters[index]?.example ||
                        sendTemplateForm.find((x) => x.type === 'body')
                          ?.parameters[index]?.text ||
                        ''
                      }
                      onChange={(e) =>
                        updateParameter('body', index, e.target.value, item)
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {buttons?.buttons?.length &&
          buttons.buttons.filter((btn) => btn?.type === 'URL').length > 0 && (
            <div className="w-100 bg-white rounded-2 p-3 py-4 d-flex flex-column gap-2 ">
              <DetailViewHeader
                title={t('whatsappTemplate.SendTemplateDetails.button')}
              />

              <div className="d-flex flex-column gap-2 mb-3">
                {buttons.buttons
                  .map((btn, originalIndex) => ({ ...btn, originalIndex })) // Attach original index
                  .filter((btn) => btn?.type === 'URL')
                  .map((button, btnIndex) => {
                    return (
                      <div
                        className="d-flex flex-column gap-1"
                        key={button.originalIndex}
                      >
                        <div className="d-flex gap-2">
                          <span className={'fw-bold'}>
                            {t(
                              'whatsappTemplate.SendTemplateDetails.buttonName'
                            )}
                            :
                          </span>
                          <span className="">{button.text}</span>
                        </div>

                        {returnPlaceholderArray(button?.url || '').map(
                          (variables, index) => {
                            const buttonForm = sendTemplateForm.find(
                              (x) =>
                                x.type === 'button' &&
                                'index' in x &&
                                x.index === button.originalIndex
                            );

                            return (
                              <div
                                key={`${button.originalIndex}-${index}`}
                                className="d-flex gap-1 w-100 text-muted align-items-center"
                              >
                                <span className="">{variables}</span>:
                                <input
                                  className="form-control form-control-sm flex-grow-1"
                                  value={
                                    buttonForm?.parameters[index]?.example ||
                                    buttonForm?.parameters[index]?.text ||
                                    ''
                                  }
                                  onChange={(e) => {
                                    updateButtonVariable(
                                      button.originalIndex,
                                      index,
                                      e.target.value,
                                      variables
                                    );
                                  }}
                                />
                              </div>
                            );
                          }
                        )}

                        <div className="text-dark">
                          <a href={button.url} target="_blank">
                            {replacePlaceholders(
                              button?.url,
                              sendTemplateForm.find(
                                (x) =>
                                  x.type === 'button' &&
                                  'index' in x &&
                                  x.index === button.originalIndex
                              )?.parameters
                            )}
                          </a>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
      </div>

      <div className="flex-fill p-1 w-100 h-100 rounded-2">
        <div
          style={{
            backgroundColor: WHATSAPP_TEMPLATE_BG_COLOR,
            overflowY: 'auto'
          }}
          className="w-100 h-100 d-flex justify-content-center align-items-center"
        >
          <div
            className={
              'd-flex flex-column justify-content-centerm align-items-center'
            }
          >
            <div style={{ width: 280 }}>
              <TemplateCard template={updatedTemplate} cardOnly={true} />
            </div>

            <div className={'text-muted mt-3 mw-400px text-center'}>
              {t('whatsappTemplate.SendTemplateDetails.previewSubtitle')}
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={() => {
          sendMessage();
        }}
        ref={submitRef}
        style={{ display: 'none' }}
      ></button>
    </div>
  );
};

export default SendTemplate;
